import * as React from "react";
import { memo, useCallback, useMemo } from "react";
import { PopoverProps } from "@mui/material/Popover";
import { useScreenSize } from "./util/useScreenSize";
import { usePatientCase } from "./usePatientCase";
import { QualifiedFeature } from "fe-common/graphql";
import { useRouteMatch } from "react-router";
import { removeLastRoute } from "../../util/pathUtils";
import { useHistory } from "react-router-dom";
import { AgentRoute } from "./util/agentRoutes";
import { useQueryParams } from "fe-web-components/useQueryParams";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { MobileSupportedContextMenu } from "./common/mobileSupportedContextMenu";
import { useGetDiseasesInPlan } from "./hooks/useGetDiseasesInPlan";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]

export interface DiseaseItemMenuProps {
  className?: string;
  id?: string;
  open: boolean;
  selectedFeature?: QualifiedFeature;
  anchorEl?: PopoverProps["anchorEl"];
  onClose: () => void;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DiseaseItemMenu = memo(
  ({ selectedFeature, ...props }: DiseaseItemMenuProps) => {
    const { isMobile } = useScreenSize();

    const { handleRemoveFromPlan, handleIncludeInPlan } = usePatientCase();

    const diseasesInPlan = useGetDiseasesInPlan();

    const match = useRouteMatch();
    // Clean "/dd" from path in the end
    const matchPath = removeLastRoute(match.path);
    const history = useHistory();
    const queryParams = useQueryParams();

    const isDiseaseInPlan = useMemo(
      () =>
        diseasesInPlan.some(
          (diseaseFeature) =>
            diseaseFeature?.disease.id === selectedFeature?.id,
        ),
      [diseasesInPlan, selectedFeature],
    );

    const planActionText = useMemo(
      () => (isDiseaseInPlan ? "Remove from Plan" : "Add to Plan"),
      [isDiseaseInPlan],
    );

    const handlePlanActionClick = useCallback(() => {
      if (selectedFeature) {
        if (isDiseaseInPlan) return handleRemoveFromPlan(selectedFeature);
        handleIncludeInPlan(selectedFeature);
      }
    }, [
      isDiseaseInPlan,
      selectedFeature,
      handleRemoveFromPlan,
      handleIncludeInPlan,
    ]);

    const handleExploreButton = useCallback(() => {
      evt({
        category: CATEGORY.AGENT_DD_LIST,
        action: ACTION.EXPLORE_DISEASE,
        label: selectedFeature.name,
      });

      if (!isMobile) {
        history.push({
          pathname: `/agent/expanded/${selectedFeature.id}`,
          search: history.location.search,
        });
        return;
      }
      history.push(
        `${matchPath}/${AgentRoute.ExpandedDisease}/${selectedFeature.id}`,
      );
    }, [isMobile, history, matchPath, selectedFeature]);

    const handleReferenceClick = useCallback(() => {
      evt({
        category: CATEGORY.AGENT_DD_LIST,
        action: ACTION.SHOW_REFERENCES,
        label: selectedFeature.name,
      });

      queryParams.set("resources", "true");
      history.push({
        pathname: `/agent/expanded/${selectedFeature?.id}`,
        search: queryParams.toString(),
      });
    }, [history, selectedFeature, queryParams]);

    const handleGraphClick = useCallback(() => {
      evt({
        category: CATEGORY.AGENT_DD_LIST,
        action: ACTION.SHOW_GRAPH,
        label: selectedFeature.name,
      });
      if (!isMobile) {
        history.push({
          pathname: `/agent/expanded/${selectedFeature?.id}`,
          search: history.location.search,
        });
        return;
      }
      history.push({
        pathname: `/agent/expanded/${selectedFeature?.id}`,
        search: history.location.search,
      });
    }, [isMobile, history, selectedFeature]);

    const desktopMenuActions = [
      {
        text: planActionText,
        action: handlePlanActionClick,
      },

      {
        text: "Explore",
        action: handleExploreButton,
      },
      {
        text: "References",
        action: handleReferenceClick,
      },
    ];

    const mobileMenuActions = [
      ...desktopMenuActions,
      {
        text: "Show graph",
        action: handleGraphClick,
      },
    ];

    return (
      <MobileSupportedContextMenu
        className={props.className}
        actions={isMobile ? mobileMenuActions : desktopMenuActions}
        open={props.open}
        id={props.id}
        onClose={props.onClose}
        anchorEl={props.anchorEl}
      />
    );
  },
);
